import { MarketingPhoneStatusBar } from './MarketingPhoneFrame';
import { TayroMark } from '../brand/TayroMark';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './HeroDashboardPreview.module.css';

/**
 * Stripped Today-dashboard snapshot used inside the landing hero
 * phone frame. Marketing-only; no real data, no interactive
 * elements. Mirrors the Claude Design landing-page mockup so the
 * marketing phone shows what a fully-populated dashboard looks like.
 *
 * Composition (top to bottom):
 *   1. Status bar (fake iOS-style, from MarketingPhoneStatusBar)
 *   2. App chrome — Tayro logo · bell · user avatar
 *   3. Greeting + countdown to championship
 *   4. Dark hero card with phase pill + EASY badge, title + subtitle,
 *      3 stats, nested coach-insight card, Start run + Ask coach
 *      visual buttons
 *   5. Two mini tiles — weather (57° overcast) + streak (12d)
 *
 * Per Phase 3 plan rule: NO real interactive elements. Buttons are
 * div/span styled to look like buttons but unfocusable.
 */
export function HeroDashboardPreview() {
  // CD-ICONS-01 — the preview mirrors the live surfaces, flag and all.
  const iconsOn = useIconSystem();
  return (
    <>
      <MarketingPhoneStatusBar />
      <div className={styles.surface}>
        {/* App chrome */}
        <div className={styles.appChrome}>
          <span className={styles.appLogo}>
            <TayroMark size={18} />
            <span>Tayro</span>
          </span>
          <span className={styles.appChromeRight}>
            <span className={styles.appBell} aria-hidden>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M11 6a4 4 0 10-8 0c0 4-2 5-2 5h12s-2-1-2-5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5.85 12.5a1.3 1.3 0 002.3 0"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                />
              </svg>
            </span>
            <span className={styles.appAvatar}>R</span>
          </span>
        </div>

        {/* Greeting + countdown */}
        <div className={styles.greeting}>
          <span className={styles.dateLine}>MON · APR 20 · LOS ALTOS</span>
          <span className={styles.greetingHi}>
            Hey, <span className={styles.greetingEm}>Runner</span>
          </span>
          <span className={styles.countdown}>
            <span className={styles.countdownNum}>30</span>{' '}
            days to <span className={styles.countdownEm}>Championship</span>
          </span>
        </div>

        {/* Dark hero card */}
        <div className={styles.heroCard}>
          <div className={styles.heroPillRow}>
            <span className={styles.heroPhasePill}>
              <span className={styles.heroDot} aria-hidden />
              Today · Week 2 · Base
            </span>
            <span className={styles.heroEasyBadge}>Easy</span>
          </div>

          <span className={styles.heroTitle}>
            Easy run, <span className={styles.heroEm}>3 miles</span>.
          </span>
          <span className={styles.heroSub}>
            Conversational pace. Nose-breathing easy.
          </span>

          <div className={styles.heroStats}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatValue}>3</span>
              <span className={styles.heroStatLabel}>miles</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatValue}>9:30</span>
              <span className={styles.heroStatLabel}>pace /mi</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatValue}>28</span>
              <span className={styles.heroStatLabel}>min approx</span>
            </div>
          </div>

          {/* Nested coach insight */}
          <div className={styles.insightInner}>
            <svg
              className={styles.insightStar}
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden
            >
              <path d="M8 1l1.7 4.2L14 6l-3.4 2.8 1.2 4.4L8 11l-3.8 2.2 1.2-4.4L2 6l4.3-.8L8 1z" />
            </svg>
            <div className={styles.insightBody}>
              <div className={styles.insightHeader}>
                <span>Training Coach</span>
                <span className={styles.insightHeaderTime}>· just now</span>
              </div>
              <span className={styles.insightText}>
                After Friday&rsquo;s 1600m setback, today resets the engine.
                Keep it conversational. We build tempo on Wednesday.
              </span>
            </div>
          </div>

          {/* Action buttons (visual only — NOT real <button>) */}
          <div className={styles.heroActions}>
            <span className={styles.heroPrimary}>
              <span className={styles.heroPrimaryPlay} aria-hidden />
              Start run
            </span>
            <span className={styles.heroSecondary}>Ask coach</span>
          </div>
        </div>

        {/* Mini tiles — weather + streak */}
        <div className={styles.tiles}>
          <div className={styles.tile}>
            {/* A2 — `sys.weather` @14; the preview mirrors the live tile. */}
            <span className={styles.tileGlyph} aria-hidden>
              {iconsOn ? <Icon name="sys.weather" size={14} /> : '☁'}
            </span>
            <span className={styles.tileLabel}>57° overcast</span>
            <span className={styles.tileValue}>57°</span>
            <span className={styles.tileSub}>wind 4 · 78%</span>
          </div>
          <div className={styles.tile}>
            <span
              className={styles.tileGlyph}
              style={{ background: 'var(--today-soft)' }}
              aria-hidden
            >
              {/* CD-ICONS-01 — the marketing hero MIRRORS the live surfaces
                  (README §Migration Map), so the streak tile takes the same
                  `chip.streak` @16 that StreakChip and RewardStrip take. */}
              {iconsOn ? <Icon name="chip.streak" size={16} /> : '🔥'}
            </span>
            <span className={styles.tileLabel}>streak</span>
            <span className={styles.tileValue}>
              12<span style={{ fontSize: 'var(--ramp-bridge-12)', marginLeft: 2, color: 'var(--ink-500)' }}>d</span>
            </span>
            <span className={styles.tileSub}>longest yet</span>
          </div>
        </div>
      </div>
    </>
  );
}
